import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, FileText, ExternalLink } from 'lucide-react';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

// Custom Rednote/Xiaohongshu icon component
const RednoteIcon = ({ className, style }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    style={style}
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-9h4v2h-4v-2zm0 3h4v2h-4v-2zm2-8c-1.1 0-2 .9-2 2h4c0-1.1-.9-2-2-2z"/>
  </svg>
);

const Footer = () => {
  const [content, setContent] = useState(null);
  const { getText, isEnglish } = useLanguage();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="mb-4">
              <img 
                src={content?.logoUrl || "https://customer-assets.emergentagent.com/job_homecarer/artifacts/v0bwves5_image.png"}
                alt="Maidful Employment Agency Limited" 
                className="h-auto w-64 object-contain"
                style={{ background: 'transparent' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_homecarer/artifacts/v0bwves5_image.png";
                }}
              />
            </div>
            <p className="text-sm font-semibold text-gray-300 mb-4">
              {getText(
                '專業代辦本地、海外菲傭、印傭聘請服務。',
                'Professional agency for local and overseas Filipino and Indonesian domestic helper services.'
              )}
            </p>

            {/* License Information */}
            {content?.licenseNumber && (
              <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="h-5 w-5" style={{ color: '#00b6a5' }} />
                  <span className="text-sm font-medium" style={{ color: '#c8ff00' }}>
                    {getText('職業介紹所牌照號碼', 'Employment Agency License No.')}
                  </span>
                </div>
                <p className="text-lg font-bold text-white">{content.licenseNumber}</p>
                {content?.licenseUrl && (
                  <a 
                    href={content.licenseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gray-400 hover:text-white flex items-center mt-1"
                  >
                    {getText('勞工處', 'Labour Department')} <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#c8ff00' }}>
              {getText('快速連結', 'Quick Links')}
            </h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('關於我們', 'About Us')}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('服務收費', 'Services')}
                </Link>
              </li>
              <li>
                <Link to="/employers" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('僱主須知', 'Employers')}
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('有用資料', 'Resources')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('常見問題', 'FAQ')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-white transition-colors">
                  {getText('聯絡我們', 'Contact Us')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#c8ff00' }}>
              {getText('我們的服務', 'Our Services')}
            </h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>{getText('海外女傭聘請', 'Overseas Helper Recruitment')}</li>
              <li>{getText('完約現成女傭', 'Contract Completion Helpers')}</li>
              <li>{getText('本地現成女傭', 'Local Available Helpers')}</li>
              <li>{getText('女傭續約服務', 'Contract Renewal Service')}</li>
              <li>{getText('簽證申請協助', 'Visa Application Assistance')}</li>
              <li>{getText('保險安排', 'Insurance Arrangement')}</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#c8ff00' }}>
              {getText('聯絡資訊', 'Contact Information')}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="h-5 w-5 mt-0.5" style={{ color: '#00b6a5' }} />
                <div>
                  <p className="text-sm font-medium">{getText('24小時客戶支援熱線', '24-Hour Customer Support')}</p>
                  <a 
                    href={`tel:${(content?.phone || '+852 6187 1388').replace(/\s/g, '')}`} 
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    {content?.phone || '+852 6187 1388'}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="h-5 w-5 mt-0.5" style={{ color: '#00b6a5' }} />
                <div>
                  <a 
                    href={`mailto:${content?.email || 'info@maidful.com.hk'}`} 
                    className="text-sm text-gray-400 hover:text-white"
                  >
                    {content?.email || 'info@maidful.com.hk'}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 mt-0.5" style={{ color: '#00b6a5' }} />
                <div>
                  <p className="text-sm text-gray-400">
                    {isEnglish 
                      ? (content?.addressEn || 'Kowloon, Hong Kong')
                      : (content?.address || '香港九龍')}
                  </p>
                </div>
              </li>
            </ul>

            {/* Social Media */}
            <div className="mt-4 flex space-x-3">
              <a
                href={content?.facebookUrl || "https://www.facebook.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                title="Facebook"
              >
                <Facebook className="h-5 w-5" style={{ color: '#00b6a5' }} />
              </a>
              <a
                href={content?.instagramUrl || "https://www.instagram.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                title="Instagram"
              >
                <Instagram className="h-5 w-5" style={{ color: '#00b6a5' }} />
              </a>
              <a
                href={content?.rednoteUrl || "https://www.xiaohongshu.com"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
                title="小紅書 Rednote"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="currentColor" 
                  className="h-5 w-5"
                  style={{ color: '#00b6a5' }}
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400">
              © 2025 {content?.companyName || '安芝僱傭中心有限公司'} {content?.companyNameEn || 'Maidful Employment Agency Limited'}. {getText('版權所有', 'All Rights Reserved')}.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">
                {getText('私隱政策', 'Privacy Policy')}
              </Link>
              <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">
                {getText('條款及細則', 'Terms & Conditions')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
